import { useRef, useState, type ChangeEvent, type DragEvent, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  FileImage,
  ImageUp,
  PhoneCall,
  ScanLine,
  ShieldAlert,
  Sparkles,
  Trash2,
  TriangleAlert,
  X,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { normalizeUrgency, isAtLeast } from "@/lib/urgency";
import { sessionQueryKey, useSession, isPatient } from "@/lib/session";
import { PageHeader } from "@/components/layout/AppShell";
import { Card, CardSection } from "@/components/ui/Card";
import { StatTile } from "@/components/ui/StatTile";
import { Button, LinkButton } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Select, Textarea } from "@/components/ui/Field";
import { Alert, UrgencyPanel } from "@/components/ui/Feedback";
import { useToast } from "@/components/ui/toast";
import { cn } from "@/lib/cn";

/**
 * MRI Assist.
 *
 * Uploads go straight to the existing /api/mri/analyze endpoint as multipart
 * form data, so the server-side quality gate, de-identification, quality
 * scoring and credit accounting are unchanged. Source images are never stored:
 * the server keeps only a SHA-256 fingerprint and the structured result.
 */

// Mirrors mri_analysis.py. Exceeding any of these client-side only wastes the
// user's time and bandwidth — the server rejects them anyway.
const MAX_FILES = 6;
const MAX_FILE_BYTES = 12 * 1024 * 1024;
const MAX_TOTAL_BYTES = 30 * 1024 * 1024;
const CREDIT_COST = 5;

const REGIONS = [
  { value: "brain", label: "Otak / kepala" },
  { value: "spine_cervical", label: "Tulang belakang servikal" },
  { value: "spine_thoracic", label: "Tulang belakang torakal" },
  { value: "spine_lumbar", label: "Tulang belakang lumbal" },
  { value: "knee", label: "Lutut" },
  { value: "shoulder", label: "Bahu" },
  { value: "abdomen", label: "Abdomen" },
  { value: "pelvis", label: "Pelvis" },
  { value: "cardiac", label: "Jantung" },
  { value: "other", label: "Area lain" },
];

const textList = z
  .union([z.string(), z.array(z.string())])
  .nullable()
  .default(null)
  .transform((value) => (value == null ? [] : Array.isArray(value) ? value : [value]));

const analysisSchema = z.looseObject({
  analysis_id: z.string(),
  ticket: z.string().nullable().default(null),
  reply: z.string().default(""),
  urgency: z.string().default("yellow"),
  images_processed: z.number().default(0),
  model: z.string().nullable().default(null),
  quality: z
    .looseObject({
      status: z.string().default("unknown"),
      average_score: z.number().nullable().default(null),
      image_count: z.number().default(0),
      limited_count: z.number().default(0),
      non_diagnostic_count: z.number().default(0),
    })
    .nullable()
    .default(null),
  result: z
    .looseObject({
      study_summary: z.string().nullable().default(null),
      observations: z
        .array(
          z.looseObject({
            finding: z.string().default(""),
            location: z.string().nullable().default(null),
            signal: z.string().nullable().default(null),
            significance: z.string().nullable().default(null),
          }),
        )
        .default([]),
      possible_interpretations: z
        .array(
          z.looseObject({
            name: z.string().default(""),
            confidence: z.string().nullable().default(null),
            supporting: textList,
            against: textList,
          }),
        )
        .default([]),
      red_flags: z
        .array(
          z.looseObject({
            finding: z.string().default(""),
            action: z.string().nullable().default(null),
            reason: z.string().nullable().default(null),
          }),
        )
        .default([]),
      recommended_next_steps: textList,
      questions_for_radiologist: textList,
      limitations: textList,
    })
    .nullable()
    .default(null),
});

type Analysis = z.infer<typeof analysisSchema>;

type Attachment = { file: File; preview: string | null };

function formatBytes(bytes: number) {
  return bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} KB`;
}

const CONFIDENCE_TONE = {
  high: "urgent",
  moderate: "monitor",
  low: "neutral",
  unknown: "neutral",
} as const;

export function MriAssist() {
  const { data: session } = useSession();
  const patient = isPatient(session) ? session : null;
  const queryClient = useQueryClient();
  const toast = useToast();

  const [consent, setConsent] = useState(false);
  const [region, setRegion] = useState("");
  const [sequence, setSequence] = useState("");
  const [context, setContext] = useState("");
  const [files, setFiles] = useState<Attachment[]>([]);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<Analysis | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const totalBytes = files.reduce((sum, item) => sum + item.file.size, 0);
  const credits = patient?.credits_remaining;
  const notEnoughCredits = credits != null && credits < CREDIT_COST;

  function addFiles(incoming: FileList | null) {
    if (!incoming) return;
    const accepted: Attachment[] = [];
    const rejected: string[] = [];
    let running = totalBytes;

    for (const file of Array.from(incoming)) {
      if (files.length + accepted.length >= MAX_FILES) {
        rejected.push(`${file.name}: melebihi ${MAX_FILES} citra`);
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        rejected.push(`${file.name}: lebih dari ${formatBytes(MAX_FILE_BYTES)}`);
        continue;
      }
      if (running + file.size > MAX_TOTAL_BYTES) {
        rejected.push(`${file.name}: total melebihi ${formatBytes(MAX_TOTAL_BYTES)}`);
        continue;
      }
      running += file.size;
      accepted.push({
        // DICOM has no browser-renderable preview; the server renders it.
        file,
        preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
      });
    }

    if (rejected.length > 0) {
      toast.warning({ title: "Sebagian berkas ditolak", description: rejected.join(" · ") });
    }
    if (accepted.length > 0) setFiles((current) => [...current, ...accepted]);
  }

  function removeFile(index: number) {
    setFiles((current) => {
      const target = current[index];
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return current.filter((_, position) => position !== index);
    });
  }

  function resetAll() {
    files.forEach((item) => item.preview && URL.revokeObjectURL(item.preview));
    setFiles([]);
    setAnalysis(null);
    setError(null);
    setContext("");
    setSequence("");
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    const payload = new FormData();
    payload.append("body_region", region);
    payload.append("sequence_hint", sequence);
    payload.append("clinical_context", context);
    payload.append("consent", "1");
    files.forEach((item) => payload.append("images", item.file));

    try {
      const result = await api.post("/api/mri/analyze", analysisSchema, payload);
      setAnalysis(result);
      // Five credits were just spent; the header badge must reflect that.
      void queryClient.invalidateQueries({ queryKey: sessionQueryKey });
    } catch (cause) {
      setError(
        cause instanceof ApiError
          ? cause.message
          : "Analisis gagal diproses. Citra Anda tidak disimpan.",
      );
    } finally {
      setBusy(false);
    }
  }

  // ---- result -----------------------------------------------------------
  if (analysis) {
    const urgency = normalizeUrgency(analysis.urgency);
    const result = analysis.result;

    return (
      <>
        <PageHeader
          eyebrow={`Analisis ${analysis.analysis_id.slice(0, 8)}`}
          title="Hasil MRI Assist"
          actions={
            <Button onClick={resetAll} icon={<ScanLine className="size-4" />}>
              Analisis baru
            </Button>
          }
        />

        {/* No `title` prop: the panel already uses the standing guidance as its
            heading, so repeating it here printed the same sentence twice. */}
        <UrgencyPanel level={urgency} className="mb-5">
          {result?.study_summary && <p>{result.study_summary}</p>}
          {isAtLeast(urgency, "critical") && (
            <a
              href="tel:119"
              className="bg-critical-500 mt-3 inline-flex h-10 items-center gap-2 rounded-(--radius-control) px-5 text-sm font-bold text-white"
            >
              <PhoneCall className="size-4" />
              Hubungi 119
            </a>
          )}
        </UrgencyPanel>

        <Alert tone="warning" title="Ini bukan pembacaan radiologi" className="mb-5">
          MRI Assist tidak divalidasi sebagai alat kesehatan, tidak membaca seluruh studi PACS, dan
          tidak dapat menyingkirkan penyakit. Hasil ini hanya bahan diskusi dengan radiolog atau
          dokter Anda.
        </Alert>

        {/* Technical quality is four numbers. As a panel of label/value rows it
            took a column to itself; as tiles it reads faster and costs one
            row. */}
        <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ["Citra diproses", `${analysis.images_processed}`],
            ["Skor rata-rata", analysis.quality?.average_score?.toFixed(0) ?? "—"],
            ["Kualitas terbatas", `${analysis.quality?.limited_count ?? 0}`],
            ["Tidak diagnostik", `${analysis.quality?.non_diagnostic_count ?? 0}`],
          ].map(([label, value]) => (
            <StatTile key={label} label={label} value={value} />
          ))}
        </div>

        <Card className="overflow-hidden">
          {result && result.red_flags.length > 0 && (
            <CardSection
              eyebrow="Prioritas"
              title="Temuan yang perlu perhatian"
              className="bg-critical-tint/40 border-t-0"
            >
                  <ul className="space-y-3">
                    {result.red_flags.map((flag, index) => (
                      <li key={index} className="flex gap-2.5">
                        <TriangleAlert
                          aria-hidden
                          className="text-critical-text mt-0.5 size-4 shrink-0"
                        />
                        <div>
                          <p className="text-sm font-semibold">{flag.finding}</p>
                          {flag.reason && (
                            <p className="text-content-muted text-xs">{flag.reason}</p>
                          )}
                          {flag.action && (
                            <p className="text-critical-text mt-0.5 text-xs font-semibold">
                              {flag.action}
                            </p>
                          )}
                        </div>
                      </li>
                    ))}
              </ul>
            </CardSection>
          )}

          {result && result.observations.length > 0 && (
            <CardSection
              eyebrow="Apa yang terlihat"
              title="Observasi visual"
              description="Deskripsi citra, belum interpretasi."
              className={cn(result.red_flags.length === 0 && "border-t-0")}
            >
              {/* Two columns: each observation is a few short lines, and one
                  per row down a full-width card wasted most of the width. */}
              <ul className="grid gap-x-8 md:grid-cols-2">
                {result.observations.map((item, index) => (
                  <li key={index} className="py-2">
                    <p className="text-sm font-medium">{item.finding}</p>
                    <p className="text-content-subtle mt-0.5 text-xs">
                      {[item.location, item.signal].filter(Boolean).join(" · ") || "—"}
                    </p>
                    {item.significance && (
                      <p className="text-content-muted mt-1 text-xs">{item.significance}</p>
                    )}
                  </li>
                ))}
              </ul>
            </CardSection>
          )}

          {result && result.possible_interpretations.length > 0 && (
            <CardSection
              eyebrow="Kemungkinan"
              title="Interpretasi"
              description="Bukan diagnosis. Setiap kemungkinan disertai bukti pendukung dan penyangkalnya."
            >
                <ul className="divide-hairline divide-y">
                  {result.possible_interpretations.map((item, index) => (
                    <li key={index} className="py-3.5 first:pt-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold">{item.name}</p>
                        <Badge
                          tone={
                            CONFIDENCE_TONE[
                              (item.confidence ?? "unknown") as keyof typeof CONFIDENCE_TONE
                            ] ?? "neutral"
                          }
                        >
                          keyakinan {item.confidence ?? "tidak diketahui"}
                        </Badge>
                      </div>
                      {item.supporting.length > 0 && (
                        <div className="mt-2">
                          <p className="eyebrow mb-1">Mendukung</p>
                          <ul className="list-disc space-y-0.5 pl-5 text-xs">
                            {item.supporting.map((line, position) => (
                              <li key={position}>{line}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {item.against.length > 0 && (
                        <div className="mt-2">
                          <p className="eyebrow mb-1">Menyangkal</p>
                          <ul className="text-content-muted list-disc space-y-0.5 pl-5 text-xs">
                            {item.against.map((line, position) => (
                              <li key={position}>{line}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                  </li>
                ))}
              </ul>
            </CardSection>
          )}

          {/* Actions on the left, caveats on the right — grid cells stretch to
              the tallest sibling, so the two sides are grouped to come out
              near the same length. */}
          {result &&
            (result.questions_for_radiologist.length > 0 ||
              result.recommended_next_steps.length > 0 ||
              result.limitations.length > 0) && (
              <div
                className={cn(
                  "grid",
                  (result.questions_for_radiologist.length > 0 ||
                    result.recommended_next_steps.length > 0) &&
                    result.limitations.length > 0 &&
                    "md:grid-cols-2",
                )}
              >
                <div className="min-w-0">
                  {result.questions_for_radiologist.length > 0 && (
                    <CardSection eyebrow="Bawa saat konsultasi" title="Pertanyaan untuk radiolog">
                      <ol className="list-decimal space-y-1.5 pl-5 text-sm">
                        {result.questions_for_radiologist.map((question, index) => (
                          <li key={index}>{question}</li>
                        ))}
                      </ol>
                    </CardSection>
                  )}

                  {result.recommended_next_steps.length > 0 && (
                    <CardSection eyebrow="Tindak lanjut" title="Langkah berikutnya">
                      <ul className="list-disc space-y-1.5 pl-5 text-sm">
                        {result.recommended_next_steps.map((step, index) => (
                          <li key={index}>{step}</li>
                        ))}
                      </ul>
                    </CardSection>
                  )}
                </div>

                {result.limitations.length > 0 && (
                  <div className="border-hairline min-w-0 md:border-l">
                    <CardSection
                      eyebrow="Keterbatasan"
                      title="Yang tidak dapat disimpulkan"
                      className="md:border-t-0"
                    >
                      <ul className="text-content-muted list-disc space-y-1.5 pl-5 text-sm">
                        {result.limitations.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </CardSection>
                  </div>
                )}
              </div>
            )}
        </Card>

        <p className="text-content-muted mt-3 flex gap-2 text-xs">
          <ShieldAlert aria-hidden className="mt-px size-3.5 shrink-0" />
          <span>
            Citra Anda <b>tidak disimpan</b>. Sistem hanya menyimpan sidik jari SHA-256, metrik
            kualitas, dan hasil terstruktur ini.
            {analysis.model && <> Model: {analysis.model}.</>}
          </span>
        </p>
      </>
    );
  }

  // ---- upload form ------------------------------------------------------
  return (
    <>
      <PageHeader
        eyebrow="Analisis citra"
        title="MRI Assist"
        description="Bantuan membaca citra MRI sebagai bahan diskusi dengan dokter. Bukan pembacaan radiologi."
        actions={
          <Badge tone={notEnoughCredits ? "critical" : "accent"}>
            {CREDIT_COST} kredit per analisis
          </Badge>
        }
      />

      {error && (
        <Alert tone="danger" title="Analisis gagal" className="mb-4">
          {error}
        </Alert>
      )}

      {notEnoughCredits && (
        <Alert tone="warning" title="Kredit tidak cukup" className="mb-4">
          MRI Assist memerlukan {CREDIT_COST} kredit, Anda memiliki {credits}.{" "}
          <LinkButton to="/patient/subscription" size="sm" className="ml-1 align-middle">
            Lihat langganan
          </LinkButton>
        </Alert>
      )}

      {/* One card, three sections. The three steps used to be three cards in a
          3/2 column split; the consent column always ran out first and left a
          large empty rectangle beside the form. */}
      <form onSubmit={submit}>
        <Card className="overflow-hidden">
          <CardSection eyebrow="Langkah 1" title="Unggah citra" className="space-y-4 border-t-0">
              <div
                onDragOver={(event: DragEvent) => {
                  event.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(event: DragEvent) => {
                  event.preventDefault();
                  setDragging(false);
                  addFiles(event.dataTransfer.files);
                }}
                className={cn(
                  "rounded-xl border border-dashed px-5 py-8 text-center transition-colors",
                  dragging ? "border-accent bg-accent/5" : "border-hairline-strong",
                )}
              >
                <ImageUp aria-hidden className="text-content-subtle mx-auto mb-2 size-6" />
                <p className="text-sm font-semibold">Tarik berkas ke sini</p>
                <p className="text-content-subtle mt-0.5 text-xs">
                  JPG, PNG, WebP, atau DICOM · maks {MAX_FILES} citra · {formatBytes(MAX_FILE_BYTES)}{" "}
                  per berkas · {formatBytes(MAX_TOTAL_BYTES)} total
                </p>
                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/webp,.dcm,application/dicom"
                  className="sr-only"
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    addFiles(event.target.files);
                    event.target.value = "";
                  }}
                />
                <Button
                  type="button"
                  className="mt-3"
                  onClick={() => inputRef.current?.click()}
                  disabled={files.length >= MAX_FILES}
                >
                  Pilih berkas
                </Button>
              </div>

              {files.length > 0 && (
                <>
                  <ul className="grid gap-2 sm:grid-cols-2">
                    {files.map((item, index) => (
                      <li
                        key={`${item.file.name}-${index}`}
                        className="border-hairline flex items-center gap-3 rounded-xl border p-2"
                      >
                        {item.preview ? (
                          <img
                            src={item.preview}
                            alt=""
                            className="bg-surface-sunken size-12 shrink-0 rounded-lg object-cover"
                          />
                        ) : (
                          <span className="bg-surface-sunken grid size-12 shrink-0 place-items-center rounded-lg">
                            <FileImage className="text-content-subtle size-5" />
                          </span>
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-xs font-medium">
                            {item.file.name}
                          </span>
                          <span className="text-content-subtle block text-2xs">
                            {formatBytes(item.file.size)}
                            {!item.preview && " · DICOM, dirender di server"}
                          </span>
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          aria-label={`Hapus ${item.file.name}`}
                          className="text-content-subtle hover:text-critical-text shrink-0"
                        >
                          <X className="size-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-content-subtle tabular">
                      {files.length}/{MAX_FILES} citra · {formatBytes(totalBytes)}/
                      {formatBytes(MAX_TOTAL_BYTES)}
                    </span>
                    <button
                      type="button"
                      onClick={resetAll}
                      className="text-content-muted hover:text-content inline-flex items-center gap-1"
                    >
                      <Trash2 className="size-3.5" />
                      Kosongkan
                    </button>
                  </div>
                </>
              )}
          </CardSection>

          {/* Two selects and a textarea sit in a row rather than a stack: as a
              full-width column they were three lonely controls with a lot of
              nothing to their right. */}
          <CardSection eyebrow="Langkah 2" title="Konteks pemeriksaan">
            <div className="grid gap-4 md:grid-cols-2">
              <Select
                label="Area pemeriksaan"
                value={region}
                onChange={(event) => setRegion(event.target.value)}
                required
              >
                <option value="" disabled>
                  Pilih area
                </option>
                {REGIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Select>

              <Select
                label="Sequence (bila diketahui)"
                value={sequence}
                onChange={(event) => setSequence(event.target.value)}
              >
                <option value="">Tidak tahu</option>
                {["T1", "T2", "FLAIR", "DWI", "STIR", "T1 kontras", "Lainnya"].map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Select>

            </div>

            <Textarea
              label="Keluhan dan konteks klinis"
              placeholder="Contoh: nyeri punggung bawah menjalar ke tungkai kanan sejak 3 bulan."
              value={context}
              onChange={(event) => setContext(event.target.value)}
              hint="Jangan tulis nama, NIK, atau nomor rekam medis."
              className="mt-4"
            />
          </CardSection>

          <CardSection eyebrow="Langkah 3" title="Persetujuan" className="space-y-4">
            <Alert tone="warning" title="Periksa identitas pada citra">
              Metadata DICOM dibersihkan otomatis, tetapi <b>nama atau nomor yang tercetak
              permanen pada gambar</b> tidak dapat dihapus sistem. Pastikan citra Anda bersih
              sebelum mengunggah.
            </Alert>

            <ul className="text-content-muted grid gap-1.5 text-xs md:grid-cols-3">
              {[
                "Citra sumber tidak disimpan — hanya sidik jari, metrik kualitas, dan hasil.",
                "Citra yang terlalu gelap atau berkontras rendah ditolak sebelum memakai kredit.",
                "Tanda bahaya pada keluhan selalu mengalahkan hasil citra.",
              ].map((item) => (
                <li key={item} className="flex gap-2">
                  <Sparkles aria-hidden className="text-accent mt-0.5 size-3.5 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap items-center justify-between gap-4">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(event) => setConsent(event.target.checked)}
                  className="accent-accent mt-0.5 size-4 shrink-0"
                />
                <span className="text-sm">
                  Saya menyetujui pemrosesan citra ini dan memahami bahwa hasilnya{" "}
                  <b>bukan pembacaan radiologi</b>.
                </span>
              </label>

              <Button
                type="submit"
                variant="primary"
                loading={busy}
                disabled={!consent || !region || files.length === 0 || notEnoughCredits}
                icon={<ScanLine className="size-4" />}
              >
                {busy ? "Menganalisis…" : `Analisis (${CREDIT_COST} kredit)`}
              </Button>
            </div>

            {busy && (
              <p className="text-content-subtle text-xs">
                Proses ini bisa memakan waktu hingga satu menit. Jangan tutup halaman.
              </p>
            )}
          </CardSection>
        </Card>
      </form>
    </>
  );
}
